import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getBundleOffers, deleteBundleOffer } from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import SearchInput from "../components/UI/SearchInput";
import ConfirmModal from "../components/UI/ConfirmModal";
import { notify } from "../utils/notify";
import DataTable from "../components/UI/DataTable";
import PageHeader from "../components/UI/PageHeader";
import { useTranslation } from "../context/I18nContext";

export default function BundleOffers() {
  const { t } = useTranslation("bundle_offers");
  const { t: tCommon } = useTranslation("common");

  function formatDateRange(startDate, endDate) {
    if (!startDate && !endDate) return t("date_range_always_on", "Always on");
    const fmt = (d) => new Date(d).toLocaleDateString();
    if (startDate && endDate) return t("date_range_between", "{start} — {end}").replace("{start}", fmt(startDate)).replace("{end}", fmt(endDate));
    if (startDate) return t("date_range_from", "From {date}").replace("{date}", fmt(startDate));
    return t("date_range_until", "Until {date}").replace("{date}", fmt(endDate));
  }

  const [bundles, setBundles] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(8);
  const [sortConfig, setSortConfig] = useState({ key: "created_at", direction: "desc" });
  const [modalConfig, setModalConfig] = useState({ isOpen: false, id: null });
  const [status, setStatus] = useState("idle");

  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const fetchBundles = async () => {
    try {
      const data = await getBundleOffers(token);
      setBundles(data || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchBundles(); }, []);

  const sortedAndFiltered = useMemo(() => {
    let result = [...bundles].filter((b) => b.name.toLowerCase().includes(searchTerm.toLowerCase()));
    result.sort((a, b) => {
      let valA = a[sortConfig.key];
      let valB = b[sortConfig.key];
      if (typeof valA === "string") valA = valA.toLowerCase();
      if (typeof valB === "string") valB = valB.toLowerCase();
      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    return result;
  }, [bundles, searchTerm, sortConfig]);

  const currentItems = sortedAndFiltered.slice((currentPage - 1) * perPage, currentPage * perPage);
  const totalPages = Math.ceil(sortedAndFiltered.length / perPage) || 1;

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
  };

  const handleDeleteClick = (id) => setModalConfig({ isOpen: true, id });

  const confirmDelete = async () => {
    try {
      setStatus("loading");
      await deleteBundleOffer(token, modalConfig.id);
      setStatus("success");
      setTimeout(() => {
        setModalConfig({ isOpen: false, id: null });
        setStatus("idle");
      }, 1200);
      notify.success(t("notify_deleted", "Bundle offer deleted successfully"));
      fetchBundles();
    } catch (err) {
      setStatus("error");
      notify.error(err.data?.error || err.message || t("notify_delete_failed", "Failed to delete bundle offer"));
    }
  };

  const columns = [
    {
      key: "name",
      label: t("col_name", "Bundle"),
      render: (row) => (
        <div className="flex items-center gap-3 font-bold">
          <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-blue-600">
            <ICONS.BundleOffers className="w-4 h-4" />
          </div>
          <div>
            <div>{row.name}</div>
            <div className="text-[10px] text-gray-400 font-normal">{t("col_products_count", "{count} product(s)").replace("{count}", (row.products || []).length)}</div>
          </div>
        </div>
      ),
    },
    {
      key: "required_quantity",
      label: t("col_offer", "Offer"),
      className: "text-gray-700 font-mono font-bold",
      render: (row) => t("offer_summary", "Buy {qty}, cheapest free").replace("{qty}", row.required_quantity),
    },
    {
      key: "start_date",
      label: t("col_active_window", "Active Window"),
      className: "text-gray-500",
      render: (row) => formatDateRange(row.start_date, row.end_date),
    },
    {
      key: "is_active",
      label: t("col_status", "Status"),
      render: (row) => (
        <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase ${row.is_active ? "bg-green-50 text-green-600" : "bg-red-50 text-red-500"}`}>
          {row.is_active ? tCommon("status_active", "Active") : tCommon("status_inactive", "Inactive")}
        </span>
      ),
    },
  ];

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto transition-all duration-300">
      <ConfirmModal
        isOpen={modalConfig.isOpen}
        title={t("delete_confirm_title", "Delete Bundle Offer")}
        message={t("delete_confirm_message", "Are you sure you want to delete this bundle offer? Its products will stop getting the free-item reward immediately.")}
        onConfirm={confirmDelete}
        onCancel={() => setModalConfig({ isOpen: false, id: null })}
        isLoading={status === "loading"}
        isSuccess={status === "success"}
        isError={status === "error"}
      />

      <PageHeader className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-6 border-b border-gray-100 pb-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/dashboard")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
            <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
          </button>
          <div className="flex items-center justify-center min-w-[48px] h-[48px] text-[var(--primary-color)]">
            <ICONS.BundleOffers size={40} className="shrink-0" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold" style={{ color: "var(--primary-color)" }}>{t("page_title", "Bundle Offers")}</h2>
            <p className="text-gray-400 text-xs md:text-sm font-medium">{t("page_subtitle", "\"Buy N, cheapest one free\" across a set of products")}</p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
          <SearchInput
            value={searchTerm}
            onChange={(val) => { setSearchTerm(val); setCurrentPage(1); }}
            placeholder={t("search_placeholder", "Search bundle offers...")}
          />
          <Button variant="primary" icon={ICONS.Plus} onClick={() => navigate("/add-bundle-offer")} className="w-full sm:w-auto shrink-0">
            {t("btn_add_bundle_offer", "Add Bundle Offer")}
          </Button>
        </div>
      </PageHeader>

      <DataTable
        columns={columns}
        data={currentItems}
        sortConfig={sortConfig}
        onSort={requestSort}
        renderActions={(row, isMobile) => (
          <>
            <Button variant="outline" size="sm" icon={ICONS.EditUser} onClick={() => navigate(`/edit-bundle-offer/${row.id}`)}>{isMobile && ` ${tCommon("btn_edit", "Edit")}`}</Button>
            <Button variant="danger" size="sm" icon={ICONS.DeleteUser} onClick={() => handleDeleteClick(row.id)}>{isMobile && ` ${tCommon("btn_delete", "Delete")}`}</Button>
          </>
        )}
        mobileRender={(row) => (
          <div className="flex justify-between items-center">
            <span className="text-[10px] text-gray-400 font-mono">{t("mobile_buy_label", "Buy {qty}").replace("{qty}", row.required_quantity)}</span>
            <h3 className="font-bold text-gray-800">{row.name}</h3>
          </div>
        )}
        paginationConfig={{
          currentPage,
          totalPages,
          onPageChange: (p) => setCurrentPage(p),
          totalItems: sortedAndFiltered.length,
        }}
      />
    </div>
  );
}
